import store  from 'store'; // Redux 스토어 import

/**
 * 재시도 로직이 포함된 fetch 함수
 * @param {string} url - 요청 URL
 * @param {object} options - fetch options
 * @param {number} retries - 재시도 횟수 (기본값: 3)
 * @param {number} timeout - 타임아웃 시간 (ms, 기본값: 10000)
 */
const fetchWithRetry = async (url, options = {}, retries = 3, timeout = 10000) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return response;
        } catch (error) {
            console.warn(`API 요청 시도 ${attempt}/${retries} 실패:`, error.message);

            if (attempt === retries) {
                throw error;
            }

            // 재시도 전 대기 (exponential backoff: 1초, 2초, 4초)
            const delay = Math.pow(2, attempt - 1) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
};

/**
 * API 목록 조회
 * @param {string} apiNm - API명
 * @returns {Promise<object>} - API 정보 (HTTP 메서드, URI 등)
 */
export const selectApiLst = async () => {
    try {
        // 한글 파라미터를 URL 인코딩 (RFC 3986 준수)
        const srvNm = encodeURIComponent('모델러모델');
        // 환경 변수 사용: 개발 환경에서는 /qcore/home, 프로덕션에서는 적절한 경로
        const baseUrl = process.env.REACT_APP_BE_API || '/home';
        const url = `${baseUrl}/mdlrmdl/selectApiLst?srv_nm=${srvNm}`;


        const response = await fetchWithRetry(url, {
            credentials: 'include',  // 세션 쿠키를 포함해야 인증 가능
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'  // AJAX 요청 마커 (AuthenticInterceptor가 확인)
            }
        }, 3, 10000);  // 3회 재시도, 10초 타임아웃

        const data = await response.json();
        // 응답 구조 호환: resultList 또는 apiLst
        const apiList = data.resultList || data.apiLst;
        return { ...data, apiLst: apiList };
    } catch (error) {
        console.error('❌ API 목록 조회 최종 실패:', error);
        throw error;
    }
};

/**
 * API 파라미터 조회
 * @param {string} apiNm - API명
 * @returns {Promise<object[]>} - API 파라미터 정보 목록
 */
/*
export const selectApiParam = async () => {
    try {
        const response = await fetch(`${REACT_APP_BE_API}/mdlrmdl/selectApiParam?api_nm=${apiNm}`);
        if (!response.ok) {
            throw new Error('API 파라미터 조회 실패');
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching API parameters:', error);
        throw error;
    }
};
*/


/**
 * API 목록 로드 대기 (최대 30초)
 * @returns {Promise<boolean>} - API 목록 로드 성공 여부
 */
const waitForApiList = async () => {
    const maxWaitTime = 30000; // 30초 (10초 → 30초로 증가)
    const checkInterval = 100; // 100ms마다 체크
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
        const apiListLoaded = store.getState().base?.common?.apiListLoaded;
        const apiList = store.getState().base?.common?.apiList;

        if (apiListLoaded && apiList && apiList.length > 0) {
            return true;
        }

        // 100ms 대기
        await new Promise(resolve => setTimeout(resolve, checkInterval));
    }

    console.error('❌ API 목록 로드 타임아웃 (30초 초과)');
    return false;
};

/**
 * 실제 API 호출 및 결과 반환
 * @param {string} apiNm - 호출할 API명
 * @param {object} params - API에 전달할 파라미터 객체
 * @returns {Promise<object>} - API 호출 결과
 */
// ApiCommon.js


  /*  API명과 파라미터내용으로 callApi 호출하면, 여기서 url 구성해서 실제 API 호출
  */
  export const callApi = async (apiNm, params = {}) => {
    try {
        // API 목록이 로드될 때까지 대기
        const apiLoaded = await waitForApiList();
        if (!apiLoaded) {
            throw new Error('API 목록을 로드할 수 없습니다. 네트워크 연결을 확인해주세요.');
        }

        const apiList = store.getState().base?.common?.apiList;
        const apiInfo = apiList.find(api => api.apiNm === apiNm);


        /*
        {srvNm: '모델러모델', apiNm: 'saveSrvcrDtls', httpMtdNm: 'POST', uriCn: '/srvcr/saveSrvcrDtls.ajax', dcCn: '서비스기준 상세 저장'}
        */

        if (!apiInfo) {
            // API 목록에서 사용 가능한 API 이름들을 출력하여 디버깅
            console.error("사용 가능한 API 목록:", apiList?.map(api => api.apiNm).join(', '));
            console.error("전체 API 목록:", apiList);
            throw new Error(`API 정보를 찾을 수 없습니다: ${apiNm}`);
        }

        // Configure request options based on HTTP method
        const requestOptions = {
            method: apiInfo.httpMtdNm || 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            }
        };

        // Handle URL and parameters based on HTTP method
        // apiInfo.uriCn이 '/qcore/home'으로 시작하지 않으면 추가
        let url = apiInfo.uriCn;
        if (!url.startsWith('/qcore/home')) {
            url = `/qcore/home${url}`;
        }

        if (apiInfo.httpMtdNm === 'GET') {
            // For GET requests, append parameters to URL as query string
            const queryString = new URLSearchParams(params).toString();
            url = queryString ? `${url}?${queryString}` : url;
        } else {
            // For non-GET requests, include parameters in request body
            requestOptions.body = JSON.stringify(params);
        }

        const response = await fetch(url, requestOptions);


        if (!response.ok) {
            // 에러 응답의 body를 읽어서 상세 정보 출력
            let errorDetail = '';
            try {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    errorDetail = JSON.stringify(errorData);
                } else {
                    errorDetail = await response.text();
                }
                console.error(`API 에러 상세 (${response.status}):`, errorDetail);
            } catch (e) {
                console.error('에러 응답 파싱 실패:', e);
            }

            if (response.status === 401) {
                console.error('인증 실패 (401): 세션이 만료되었거나 로그인이 필요합니다.');
                // 사용자에게 알림 표시
                const event = new CustomEvent('auth-error', {
                    detail: {
                        message: '세션이 만료되었습니다. 다시 로그인해주세요.',
                        status: 401
                    }
                });
                window.dispatchEvent(event);
                throw new Error(`인증 실패: ${response.statusText}`);
            }
            throw new Error(`API 호출 실패 (${response.status}): ${response.statusText}\n${errorDetail}`);
        }

        const result = await response.json();
        return result;

    } catch (error) {
        console.error(`API 호출 중 오류 발생 (${apiNm}):`, error);
        throw error;
    }
};

